export type FieldDef =
  | { kind: "string" | "text" | "image" | "video" | "boolean"; key: string; label: string; hint?: string }
  | { kind: "stringList"; key: string; label: string; itemName: string }
  | { kind: "object"; key: string; label: string; fields: FieldDef[] }
  | { kind: "list"; key: string; label: string; itemName: string; fields: FieldDef[] };

export interface SectionDef {
  key: string;
  label: string;
  fields: FieldDef[];
}

const ICON_HINT = "Lucide 圖標名，例如：Bot / Camera / Clapperboard / BadgeCheck / Fingerprint / Users / Share2 / Handshake / FileText / GraduationCap / Ticket / Facebook / Instagram / BookOpen / Music2 / Video";

export const SECTIONS: SectionDef[] = [
  {
    key: "site",
    label: "全站設定",
    fields: [
      { kind: "string", key: "title", label: "網站標題" },
      { kind: "text", key: "description", label: "網站描述" },
      { kind: "string", key: "logo", label: "Logo 文字" },
      { kind: "image", key: "logoIcon", label: "Logo 圖標" },
      { kind: "image", key: "logoFull", label: "完整 Logo" },
    ],
  },
  {
    key: "navigation",
    label: "導覽列",
    fields: [
      {
        kind: "list",
        key: "navigation",
        label: "導覽連結",
        itemName: "連結",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "label", label: "文字" },
          { kind: "string", key: "href", label: "連結", hint: "錨點格式：#section" },
        ],
      },
    ],
  },
  {
    key: "hero",
    label: "Hero 主視覺",
    fields: [
      { kind: "string", key: "badge", label: "徽章文字" },
      { kind: "string", key: "headline", label: "主標題" },
      { kind: "string", key: "highlight", label: "高亮文字" },
      { kind: "video", key: "video", label: "主視覺短片", hint: "支援 MP4 / WebM，只允許一條，將按短片原始尺寸置中顯示" },
      { kind: "text", key: "subheadline", label: "副標題" },
      { kind: "stringList", key: "features", label: "特色標籤", itemName: "標籤" },
      { kind: "string", key: "ctaText", label: "CTA 文字" },
      { kind: "string", key: "ctaSubtext", label: "CTA 副文字" },
      { kind: "text", key: "whatsappMessage", label: "WhatsApp 預設訊息" },
      { kind: "image", key: "backgroundImage", label: "背景圖片" },
    ],
  },
  {
    key: "dualMode",
    label: "雙模式製作",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "text", key: "subtitle", label: "副標題" },
      {
        kind: "list",
        key: "modes",
        label: "兩種模式",
        itemName: "模式",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "icon", label: "Icon", hint: ICON_HINT },
          { kind: "string", key: "title", label: "標題" },
          { kind: "string", key: "tagline", label: "標語" },
          { kind: "text", key: "description", label: "描述" },
        ],
      },
      { kind: "string", key: "missionLabel", label: "使命標籤" },
      { kind: "text", key: "missionText", label: "使命內容" },
      { kind: "string", key: "ctaText", label: "CTA 文字" },
      { kind: "text", key: "whatsappMessage", label: "WhatsApp 訊息" },
    ],
  },
  {
    key: "painPoints",
    label: "痛點分析",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "text", key: "intro", label: "介紹文字" },
      {
        kind: "list",
        key: "items",
        label: "痛點項目",
        itemName: "項目",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "problem", label: "痛點" },
          { kind: "text", key: "problemDesc", label: "痛點描述" },
          { kind: "string", key: "solution", label: "解決方案" },
          { kind: "text", key: "solutionDesc", label: "方案描述" },
        ],
      },
      { kind: "string", key: "solutionTitle", label: "解決方案標題" },
      { kind: "string", key: "ctaText", label: "CTA 文字" },
      { kind: "text", key: "whatsappMessage", label: "WhatsApp 訊息" },
    ],
  },
  {
    key: "services",
    label: "核心服務",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "text", key: "intro", label: "介紹文字" },
      {
        kind: "list",
        key: "services",
        label: "服務項目",
        itemName: "服務",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "icon", label: "Icon", hint: ICON_HINT },
          { kind: "string", key: "title", label: "標題" },
          { kind: "string", key: "tagline", label: "標語" },
          { kind: "text", key: "description", label: "描述" },
          { kind: "image", key: "image", label: "服務圖片" },
          { kind: "stringList", key: "tags", label: "標籤", itemName: "標籤" },
        ],
      },
    ],
  },
  {
    key: "aiFeature",
    label: "AI 數字人",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "text", key: "description", label: "描述" },
      { kind: "stringList", key: "fears", label: "痛點標籤", itemName: "標籤" },
      { kind: "string", key: "solution", label: "解決方案標題" },
      { kind: "stringList", key: "capabilities", label: "能力標籤", itemName: "標籤" },
      { kind: "string", key: "ctaText", label: "CTA 文字" },
      { kind: "text", key: "whatsappMessage", label: "WhatsApp 訊息" },
    ],
  },
  {
    key: "teamExpertise",
    label: "團隊專業",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "text", key: "description", label: "描述" },
      {
        kind: "list",
        key: "expertise",
        label: "專業項目",
        itemName: "項目",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "icon", label: "Icon", hint: ICON_HINT },
          { kind: "string", key: "label", label: "標籤" },
        ],
      },
      { kind: "text", key: "closingLine", label: "結尾句" },
    ],
  },
  {
    key: "portfolio",
    label: "作品集",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      {
        kind: "list",
        key: "platforms",
        label: "平台篩選",
        itemName: "平台",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "label", label: "名稱" },
        ],
      },
      {
        kind: "list",
        key: "items",
        label: "作品項目",
        itemName: "作品",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "title", label: "標題" },
          { kind: "string", key: "platform", label: "平台 ID" },
          { kind: "string", key: "views", label: "觀看數" },
          { kind: "image", key: "thumbnail", label: "縮圖" },
          { kind: "string", key: "href", label: "連結" },
        ],
      },
      { kind: "string", key: "ctaText", label: "CTA 文字" },
      { kind: "text", key: "whatsappMessage", label: "WhatsApp 訊息" },
    ],
  },
  {
    key: "businessScope",
    label: "業務範圍",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "string", key: "companyName", label: "公司名稱" },
      {
        kind: "list",
        key: "items",
        label: "業務項目",
        itemName: "項目",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "icon", label: "Icon", hint: ICON_HINT },
          { kind: "string", key: "label", label: "名稱" },
        ],
      },
      { kind: "string", key: "ctaText", label: "CTA 文字" },
      { kind: "text", key: "whatsappMessage", label: "WhatsApp 訊息" },
    ],
  },
  {
    key: "pricing",
    label: "收費方案",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "text", key: "intro", label: "介紹文字" },
      {
        kind: "list",
        key: "plans",
        label: "方案",
        itemName: "方案",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "name", label: "名稱" },
          { kind: "string", key: "stage", label: "階段" },
          { kind: "string", key: "priceLabel", label: "價格標籤" },
          { kind: "string", key: "unit", label: "單位" },
          { kind: "boolean", key: "recommended", label: "是否推薦", hint: "true 或 false" },
          { kind: "string", key: "tagline", label: "標語" },
          { kind: "stringList", key: "features", label: "方案內容", itemName: "項目" },
          { kind: "text", key: "whatsappMessage", label: "WhatsApp 訊息" },
        ],
      },
    ],
  },
  {
    key: "contact",
    label: "聯絡我們",
    fields: [
      { kind: "string", key: "sectionTitle", label: "區塊標題" },
      { kind: "string", key: "subtitle", label: "副標題" },
      { kind: "string", key: "whatsapp", label: "WhatsApp 號碼", hint: "例如 85298239693" },
      { kind: "string", key: "whatsappDisplay", label: "WhatsApp 顯示文字" },
      { kind: "string", key: "wechat", label: "WeChat ID" },
      { kind: "string", key: "consultant", label: "對接顧問" },
      { kind: "string", key: "companyName", label: "公司名稱" },
      { kind: "string", key: "companyNameEn", label: "公司英文名稱" },
      { kind: "text", key: "defaultMessage", label: "預設 WhatsApp 訊息" },
      { kind: "image", key: "qrCode", label: "WhatsApp QR Code" },
      { kind: "image", key: "logo", label: "聯絡區 Logo" },
    ],
  },
  {
    key: "footer",
    label: "頁尾",
    fields: [
      { kind: "text", key: "copyright", label: "版權文字" },
      { kind: "image", key: "logo", label: "頁尾 Logo" },
      {
        kind: "list",
        key: "socials",
        label: "社群連結",
        itemName: "平台",
        fields: [
          { kind: "string", key: "id", label: "ID" },
          { kind: "string", key: "platform", label: "平台名稱" },
          { kind: "string", key: "url", label: "URL" },
          { kind: "string", key: "icon", label: "Icon", hint: ICON_HINT },
        ],
      },
    ],
  },
];
